/*
 * Peter Beales Roses - storefront enhancements
 * ---------------------------------------------------------------------------
 * Small, dependency-free behaviours that need to run everywhere (including
 * inside the AJAX-loaded quick-buy modal, whose inline <script> tags are not
 * executed by the theme). Loaded once, globally, from layout/theme.liquid.
 *
 * Currently:
 *   - prefers-reduced-motion: forces programmatic scrolls to jump, not glide.
 *   - Bare-root dispatch notice: shows snippets/bare-root-notice.liquid when
 *     a "Bare Root" variant is selected.
 *   - <cart-reward>: second cart incentive tier above free shipping.
 *
 * Key Information: empty rows are now dropped in Liquid
 * (snippets/pb-key-information.liquid), so no client-side cleanup is needed.
 */
(function () {
  'use strict';

  /* ---------- Reduced motion: neutralise smooth programmatic scrolling ----------
     CSS scroll-behavior only covers CSS-driven scrolls; JS calls that pass
     {behavior:'smooth'} explicitly ignore it. When the OS asks for reduced
     motion we downgrade those to an instant jump - theme-wide, once. */
  (function () {
    if (!window.matchMedia) return;
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!mq.matches) return;

    var flatten = function (opts) {
      if (opts && typeof opts === 'object' && opts.behavior === 'smooth') {
        var copy = {};
        for (var k in opts) if (Object.prototype.hasOwnProperty.call(opts, k)) copy[k] = opts[k];
        copy.behavior = 'auto';
        return copy;
      }
      return opts;
    };

    var sivProto = Element.prototype.scrollIntoView;
    if (sivProto && !sivProto.__pbFlat) {
      Element.prototype.scrollIntoView = function (opts) { return sivProto.call(this, flatten(opts)); };
      Element.prototype.scrollIntoView.__pbFlat = true;
    }
    ['scrollTo', 'scroll'].forEach(function (name) {
      [window, Element.prototype].forEach(function (ctx) {
        var orig = ctx[name];
        if (typeof orig !== 'function' || orig.__pbFlat) return;
        ctx[name] = function () {
          if (arguments.length === 1) return orig.call(this, flatten(arguments[0]));
          return orig.apply(this, arguments);
        };
        ctx[name].__pbFlat = true;
      });
    });
  })();

  /* ---------- Cart reward tier ---------- */

  function formatCartMoney(cents) {
    var amount = cents / 100;
    try {
      var iso = window.Shopify && Shopify.currency && Shopify.currency.active;
      if (iso) return amount.toLocaleString(undefined, { style: 'currency', currency: iso });
    } catch (e) {}
    return '£' + amount.toFixed(2);
  }

  if (!customElements.get('cart-reward')) {
    customElements.define('cart-reward', class extends HTMLElement {
      connectedCallback() {
        var threshold = Number(this.dataset.threshold);
        var total = Number(this.dataset.cartTotal);
        if (!threshold || isNaN(total) || total <= 0) { this.hidden = true; return; }

        var rate = 1;
        try {
          if (window.Shopify && Shopify.currency && Shopify.currency.rate) rate = Number(Shopify.currency.rate);
        } catch (e) {}
        var target = Math.round(threshold * rate);
        var remaining = target - total;
        var label = this.dataset.label || 'a free gift';

        if (remaining > 0) {
          this.classList.remove('cart-reward--unlocked');
          var pct = Math.max(4, Math.min(100, Math.round((total / target) * 100)));
          var msg = (this.dataset.progress || 'Spend [remaining] more to get [reward]')
            .replace('[remaining]', formatCartMoney(remaining))
            .replace('[reward]', label);
          this.innerHTML = this.#escapeText(msg) +
            '<span class="cart-reward__bar"><span class="cart-reward__bar-fill" style="width:' + pct + '%"></span></span>';
        } else {
          this.classList.add('cart-reward--unlocked');
          var done = (this.dataset.success || "You've unlocked [reward]").replace('[reward]', label);
          this.innerHTML = this.#escapeText(done);
        }
        this.hidden = false;
      }

      #escapeText(str) {
        var d = document.createElement('div');
        d.textContent = str;
        return d.innerHTML;
      }
    });
  }

  /* ---------- Bare-root dispatch notice ---------- */

  function variantIsBareRoot(variant) {
    if (!variant) return false;
    if (Array.isArray(variant.options) && variant.options.indexOf('Bare Root') !== -1) return true;
    return typeof variant.title === 'string' && /bare\s*root/i.test(variant.title);
  }

  function readVariant(el) {
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function initNotice(note) {
    if (note.hasAttribute('data-bound')) return;
    note.setAttribute('data-bound', '');

    var containerId = note.getAttribute('data-container');
    var container = (containerId && document.getElementById(containerId)) || note.closest('[data-js-product-component]') || document;
    var dataEl = container.querySelector('[data-js-variant-data]');
    if (dataEl) note.hidden = !variantIsBareRoot(readVariant(dataEl));
  }

  function initAllNotices(root) {
    (root || document).querySelectorAll('[data-bare-root-notice]').forEach(initNotice);
  }

  document.addEventListener('krown:variant:change', function (e) {
    var variant = e && e.detail && e.detail.variant;
    if (!variant) return;
    document.querySelectorAll('[data-bare-root-notice]').forEach(function (note) {
      var pid = note.getAttribute('data-product-id');
      if (pid && variant.product_id && String(variant.product_id) !== String(pid)) return;
      note.hidden = !variantIsBareRoot(variant);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAllNotices(); });
  } else {
    initAllNotices();
  }

  // Quick-buy modal is injected after load - pick up notices as they appear.
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-bare-root-notice]')) initNotice(node);
          if (node.querySelectorAll) initAllNotices(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

/* ---------------------------------------------------------------------------
   Gift message (cart page) - snippets/pb-gift-message.liquid
   Saves "Gift order / Gift to / Gift message / Gift from" as cart attributes
   so they travel through checkout onto the order. Unticking clears them.
--------------------------------------------------------------------------- */
(function () {
  if (customElements.get('pb-gift-message')) return;
  var KEYS = { to: 'Gift to', message: 'Gift message', from: 'Gift from', flag: 'Gift order', note: 'Gift note' };

  customElements.define('pb-gift-message', class extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.toggle = this.querySelector('[data-gift-toggle]');
      this.fieldsWrap = this.querySelector('[data-gift-fields]');
      this.flag = this.querySelector('[data-gift-flag]');
      this.status = this.querySelector('[data-gift-status]');
      this.count = this.querySelector('[data-gift-count]');
      this.message = this.querySelector('#PbGiftMessage');
      this.max = parseInt(this.dataset.max, 10) || 250;
      this.timer = null;

      this.toggle.addEventListener('change', () => {
        var on = this.toggle.checked;
        this.fieldsWrap.hidden = !on;
        this.classList.toggle('is-open', on);
        this.flag.value = on ? 'Yes' : '';
        if (on) {
          var first = this.querySelector('#PbGiftTo');
          if (first) first.focus();
        }
        this.save(0);
      });
      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-gift-field]')) {
          this.updateCount();
          this.save(500);
        }
      });
      this.updateCount();
    }

    updateCount() {
      if (!this.count || !this.message) return;
      var left = this.max - this.message.value.length;
      this.count.textContent = left + ' characters left';
    }

    values() {
      var on = this.toggle.checked;
      var get = (id) => { var el = this.querySelector(id); return on && el ? el.value.trim() : ''; };
      var attrs = {};
      attrs[KEYS.flag] = on ? 'Yes' : '';
      attrs[KEYS.to] = get('#PbGiftTo');
      attrs[KEYS.message] = get('#PbGiftMessage');
      attrs[KEYS.from] = get('#PbGiftFrom');
      // One combined field for Shiptheory, which can only read a single
      // attribute (Shiptheory > Shopify settings > Advanced Settings >
      // Save Order Gift Messages > Custom Attribute Name = "Gift note").
      var lines = [];
      if (attrs[KEYS.to]) lines.push('To: ' + attrs[KEYS.to]);
      if (attrs[KEYS.message]) lines.push(attrs[KEYS.message]);
      if (attrs[KEYS.from]) lines.push('From: ' + attrs[KEYS.from]);
      attrs[KEYS.note] = on ? lines.join(String.fromCharCode(10)) : '';
      return attrs;
    }

    save(delay) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        var url = (window.KROWN && KROWN.settings && KROWN.settings.routes && KROWN.settings.routes.cart_update_url) || '/cart/update.js';
        if (url.slice(-3) !== '.js') url += '.js';
        this.setStatus('Saving\u2026');
        // Queue saves so they reach Shopify strictly in order - otherwise a
        // slow earlier save can land after an untick and restore the message.
        this.queue = (this.queue || Promise.resolve()).then(() =>
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ attributes: this.values() })
          }).then((r) => {
            this.setStatus(r.ok ? (this.toggle.checked ? 'Gift message saved' : '') : 'Could not save - please try again');
          }).catch(() => this.setStatus('Could not save - please try again'))
        );
      }, delay);
    }

    setStatus(text) { if (this.status) this.status.textContent = text; }
  });
})();
